import { mediaQueryLarge, requestIdleCallback, startViewTransition } from '@theme/utilities';
import PaginatedList from '@theme/paginated-list';

/**
 * A custom element that renders a pagniated results list
 */
export default class ResultsList extends PaginatedList {
  #collectionImageObserver = new MutationObserver(() => this.#syncCollectionImageSizes());

  #syncCollectionImageSizes() {
    const grid = this.refs.grid;
    if (!grid) return;
    const singleColumn = !mediaQueryLarge.matches && grid.getAttribute('product-grid-view') === 'mobile-single';
    grid.querySelectorAll('img').forEach((image) => {
      if (!image.dataset.ggCollectionSizes && image.sizes.includes('calc(50vw - 12px)')) {
        image.dataset.ggCollectionSizes = image.sizes;
      }
      const original = image.dataset.ggCollectionSizes;
      if (!original) return;
      const sizes = singleColumn ? original.replace('calc(50vw - 12px)', '100vw') : original;
      if (image.sizes !== sizes) image.sizes = sizes;
    });
  }

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange);
    this.#syncCollectionImageSizes();
    this.#collectionImageObserver.observe(this, { childList: true, subtree: true, attributes: true, attributeFilter: ['product-grid-view'] });
    this.setAttribute('initialized', '');
  }

  disconnectedCallback() {
    this.#collectionImageObserver.disconnect();
    mediaQueryLarge.removeEventListener('change', this.#handleMediaQueryChange);
  }

  /**
   * Updates the layout.
   *
   * @param {Event} event
   */
  updateLayout({ target }) {
    if (!(target instanceof HTMLInputElement)) return;

    this.#animateLayoutChange(target.value);
  }

  /**
   * Sets the layout.
   *
   * @param {string} value
   */
  #animateLayoutChange = async (value) => {
    const { grid } = this.refs;

    if (!grid) return;

    await startViewTransition(() => this.#setLayout(value), ['product-grid']);

    requestIdleCallback(() => {
      const viewport = mediaQueryLarge.matches ? 'desktop' : 'mobile';
      sessionStorage.setItem(`product-grid-view-${viewport}`, value);
    });
  };

  /**
   * Animates the layout change.
   *
   * @param {string} value
   */
  #setLayout(value) {
    const { grid } = this.refs;
    if (!grid) return;
    grid.setAttribute('product-grid-view', value);
  }

  /**
   * Handles the media query change event.
   *
   * @param {MediaQueryListEvent} event
   */
  #handleMediaQueryChange = (event) => {
    const targetElement = event.matches
      ? this.querySelector('[data-grid-layout="desktop-default-option"]')
      : this.querySelector('[data-grid-layout="mobile-option"]');

    if (!(targetElement instanceof HTMLInputElement)) return;

    targetElement.checked = true;
    this.#setLayout('default');
  };
}

if (!customElements.get('results-list')) {
  customElements.define('results-list', ResultsList);
}
